// Footer.jsx
import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Activity, Heart, ChevronRight } from "lucide-react";
import LogoMikDok from "./components/common/LogoMikDok";

// Helper: build URL like /?city=Tirana&q=kardiolog  OR keep current path if you prefer
function buildSearchUrl({ city, q, spec }) {
  const sp = new URLSearchParams();
  if (city) sp.set("city", city);
  if (q) sp.set("q", q);
  if (spec) sp.set("spec", spec);
  const qs = sp.toString();
  return `/${qs ? `?${qs}` : ""}`;
}

export default function SiteFooter({ t }) {
  const navigate = useNavigate();
  const location = useLocation();

  // You can customize these sitelinks however you want:
  // - q: symptom/service keyword that matches your searchCorpus
  // - city: prefill city input
  // - spec: prefill specialty dropdown (must match exactly your doctor.specialty values)
  const sitelinks = useMemo(
    () => [
      { titleSq: "Kardiolog në Tiranë", titleEn: "Cardiologist in Tirana", city: "Tirana", q: "kardiolog" },
      { titleSq: "Dermatolog në Tiranë", titleEn: "Dermatologist in Tirana", city: "Tirana", q: "dermatolog" },
      { titleSq: "Ortoped në Tiranë", titleEn: "Orthopedist in Tirana", city: "Tirana", q: "ortoped" },
      { titleSq: "Gjinekolog në Tiranë", titleEn: "Gynecologist in Tirana", city: "Tirana", q: "gjinekolog" },
      { titleSq: "Pediatër në Tiranë", titleEn: "Pediatrician in Tirana", city: "Tirana", q: "pediater" },
      { titleSq: "Okulist në Tiranë", titleEn: "Ophthalmologist in Tirana", city: "Tirana", q: "okulist" },

      { titleSq: "Dentist në Durrës", titleEn: "Dentist in Durres", city: "Durres", q: "dentist" },
      { titleSq: "ENT në Tiranë", titleEn: "ENT in Tirana", city: "Tirana", q: "otorinolaringolog" },

      // examples using specialty dropdown (spec must match doctor.specialty exactly)
      // { titleSq: "Kardiologji (çdo qytet)", titleEn: "Cardiology (any city)", spec: "Cardiology" },
    ],
    []
  );

  const lang = (t?.mode === "Mënyra" ? "sq" : undefined) || (t?.login === "Hyr" ? "sq" : "en"); // quick heuristic

  const onQuickSearch = (x) => {
    const url = buildSearchUrl({ city: x.city, q: x.q, spec: x.spec });
    // navigate to landing page with query params
    navigate(url, { replace: false });

    // If you want to scroll to results automatically:
    // Wait next tick for page render, then scroll
    setTimeout(() => document.getElementById("results")?.scrollIntoView({ behavior: "smooth" }), 0);
  };

  return (
    <>
      <footer className="mdk-footer" role="contentinfo">
        <div className="mdk-container">
          <div className="mdk-footer-grid">
            {/* Brand */}
            <div className="mdk-footer-col brand">
              <button
                type="button"
                className="mdk-footer-brandbtn"
                onClick={() => navigate("/")}
                aria-label="MikDok home"
              >
               
              <LogoMikDok
  showText
  subtitle={`© ${new Date().getFullYear()} • ${t?.allRights ?? "All rights reserved"}`}
  badge={50}
  size={35}
  markColor="#ffffff"
/>                
              </button>

              <p className="mdk-footer-sub">
                Made with{" "}
                <Heart size={14} style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                for better healthcare
              </p>

              {/* Optional tiny “status” row */}
              <div className="mdk-footer-badges">
                <span className="mdk-footer-pill">{lang === "sq" ? "Rezervo pa telefonata" : "Book without calls"}</span>
                <span className="mdk-footer-pill">{lang === "sq" ? "Profesionistë të verifikuar" : "Verified professionals"}</span>
              </div>
            </div>

            {/* Legal + company */}
            <div className="mdk-footer-col">
              <div className="mdk-footer-h">{lang === "sq" ? "Informacion" : "Info"}</div>
              <nav className="mdk-footer-links" aria-label="Footer info">
                <Link className="mdk-footer-link" to="/privacy">
                  {t?.privacy ?? "Privacy"}
                </Link>
                <Link className="mdk-footer-link" to="/terms">
                  {t?.terms ?? "Terms"}
                </Link>
               

                <button
                  type="button"
                  className="mdk-footer-link mdk-footer-cookie"
                  onClick={() => {
                    if (typeof window !== "undefined" && typeof window.openCookieSettings === "function") {
                      window.openCookieSettings();
                    } else {
                      document.dispatchEvent(new CustomEvent("mikdok:open-cookie-settings"));
                    }
                  }}
                  aria-label="Cookie settings"
                  title="Cookie settings"
                >
                  {lang === "sq" ? "Cilësimet e cookies" : "Cookie settings"}
                </button>
              </nav>
            </div>

            {/* Popular searches / sitelinks */}
            <div className="mdk-footer-col">
              <div className="mdk-footer-h">{lang === "sq" ? "Kërkime të shpejta" : "Popular searches"}</div>

              <div className="mdk-footer-sitelinks" aria-label="Quick searches">
                {sitelinks.slice(0, 8).map((x, i) => (
                  <button
                    key={`${x.city || ""}-${x.q || ""}-${x.spec || ""}-${i}`}
                    type="button"
                    className="mdk-footer-sitelink"
                    onClick={() => onQuickSearch(x)}
                    title={lang === "sq" ? x.titleSq : x.titleEn}
                  >
                    <span className="mdk-footer-sitelink-t">
                      {lang === "sq" ? x.titleSq : x.titleEn}
                    </span>
                    <ChevronRight size={16} aria-hidden="true" />
                  </button>
                ))}
              </div>

             
            </div>
          </div>

          <div className="mdk-footer-bottom">
            <span className="mdk-footer-small">
              {lang === "sq"
                ? "MikDok nuk zëvendëson konsultën mjekësore. Në emergjencë, kontakto 127."
                : "MikDok doesn’t replace medical advice. In emergencies, call local services."}
            </span>

            
          </div>
        </div>
      </footer>

      {/* Push main content above sticky mobile CTA */}
      <div className="mdk-bottom-spacer" />
    </>
  );
}
